import type { SearchBoxVM } from '@/react/view-models/SearchBoxVM';
import type { VoidCallback } from '@/type-utils';
import { observer } from 'mobx-react-lite';
import { use } from 'react';
import { msg } from '@/services/isomorphic/I18NService';
import { ProductSuggestionTile } from '../ProductSuggestionTile';

import S from './styles.module.scss';
import { search_box_frame_suggestedProducts } from "@/lang/__generated__/ahnu/search_box_frame_suggestedProducts";

export interface ISuggestedProductsProps {
  /**
   * The `SearchBoxVM` instance to be used.
   */
  vm: SearchBoxVM;

  /**
   * The function to be called when the user navigates to a new page.
   */
  onNavigate: VoidCallback;
}

/**
 * A component that displays a list of suggested products when no search is inputted on `SearchBoxVM`.
 *
 * Or if a search input is present but no results are found. See {@link SearchBoxVM.productSuggestions} for more details.
 */
export const SuggestedProducts = observer(function SuggestedProducts({
  vm,
  onNavigate
}: ISuggestedProductsProps) {
  const productSuggestions =
    vm.productSuggestions instanceof Promise
      ? use(vm.productSuggestions)
      : vm.productSuggestions;

  return (
    <>
      <h3 className={S.title}>{msg(search_box_frame_suggestedProducts)}</h3>
      <div className={S.productsRow}>
        {productSuggestions.map((suggestion, index) => (
          <ProductSuggestionTile
            // Composing the key using the suggestion SKU to ensure uniqueness
            key={`initial-suggestion-${suggestion.sku}`}
            suggestion={suggestion}
            className={S.suggestedTile}
            onClick={() => {
              // Index is 0-based, so the position we send to the analytics needs an increment
              vm.dispatchProductClickAnalyticsEvent(suggestion, index + 1);
              onNavigate();
            }}
          />
        ))}
      </div>
    </>
  );
});
